import { daysOfWeek } from '../../components/limit/LimitUtils';
import type { Limit } from '../services/LimitService/types';
import type { PartnerCostAdjustConfig, PartnerLimitCreateV2, PartnerLimitV2 } from '@shipium-corp/shipium-ts-types';

export type CostAdjustTargetBehavior = NonNullable<PartnerCostAdjustConfig['costAdjustTargetBehavior']>;

export const DEFAULT_LIMIT_BEHAVIOR: CostAdjustTargetBehavior = 'above';

export const LIMIT_BEHAVIOR_LABEL: Record<CostAdjustTargetBehavior, string> = {
  above: 'Keep Above Target',
  at: 'Keep At Target',
  below: 'Keep Below Target',
};

// functions that allow transform v1 and v2 limits to the same interface so that we can continue using the same page
// for v1 and v2 limits

export const modifyV2CostingLimitToV1 = (lim: PartnerLimitV2): Limit => {
  return {
    archive: false,
    active: true,
    limitId: lim.limitId,
    localZoneId: lim.localZoneId,
    localCutoffTime: lim.localCutoffTime,
    startDate: lim.effectiveDate,
    constraint: {
      upgradeSpendThreshold: lim?.constraints?.[0]?.thresholdValue,
      type: 'maximum',
    },
  };
};

export const modifyV1CostingLimitToV2 = (lim: Limit): PartnerLimitCreateV2 => {
  return {
    constraints: daysOfWeek.map((dow) => {
      return {
        constraintType: 'maximum',
        dayOfWeek: dow as any,
        thresholdType: 'upgrade_spend',
        thresholdValue: lim?.constraint?.upgradeSpendThreshold ?? 0,
      };
    }),
    effectiveDate: new Date().toISOString(),
    localCutoffTime: lim.localCutoffTime,
    localZoneId: lim.localZoneId,
    name: 'global cost limit',
  };
};

// Dynamic limits significant digits after the decimal
const DYNAMIC_LIMITS_SIG_DIGITS = 4;

// 18.287 -> 1829 -> 0.1829
export function percentToClampedFixedDouble(val: number) {
  const clamped = Math.max(Math.min(val, 100), 0);
  const powerOfTen = Math.pow(10, DYNAMIC_LIMITS_SIG_DIGITS - 2);
  const decimal = Math.round(clamped * powerOfTen) / powerOfTen / 100;

  return decimal;
}

// 0.18294 -> 1829 -> 18.29
export function doubleToPercent(val: number | string) {
  const powerOfTen = Math.pow(10, DYNAMIC_LIMITS_SIG_DIGITS);
  const rounded = Math.round(+val * powerOfTen);
  const percent = (rounded * 100) / powerOfTen;

  return percent;
}

// Undefined behavior is treated as 'above' (legacy default for configs that
// pre-date the costAdjustTargetBehavior field).
export function formatLimitBehavior(
  behavior: CostAdjustTargetBehavior | undefined,
  targetPercent: number | undefined,
): string {
  if (targetPercent === undefined || targetPercent === null) return '';
  const target = doubleToPercent(targetPercent);
  if (behavior === 'below') return `Keep Below ${target}%`;
  if (behavior === 'at') return `Keep At ${target}%`;
  return `Keep Above ${target}%`;
}
